/* eslint-disable react/no-array-index-key */
import {
  Grid,
  Typography,
  Button,
  CircularProgress,
  Tooltip,
  Theme,
  useMediaQuery,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { RootState } from '@Common/models/redux/store';
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BottomScrollListener } from 'react-bottom-scroll-listener';
import { generateRandomId, getShortUrl } from 'core/utils';
import { useRouter } from 'next/router';
import { generateCitations } from 'src/researcher/handlers/api';
import { openToastr } from '@Common/handlers/services';
import {
  APP_BAR_HEIGHT,
  TinyToolbarContainerProps,
} from 'src/researcher/constants/common.constants';
import { useCitationGeneratorDispatch, useCitationGeneratorState } from '../context';
import {
  incrementPageNo,
  stopSearchMode,
  toggleCitationGeneratorEditModal,
  toggleInplaceCreateForm,
} from '../context/actionCreators';
import { CitationData, ContributorTypes, CitationSourceEnum } from '../types';
import {
  addCitationToList,
  rebuildCitationNumbers,
  updateCitedData,
} from '../reducers/actionCreators';
import citationGeneratorTrack from '../handlers/track';
import { TriggeringActions } from '../types/names';
import {
  convertCitedDataResponse,
  getCgBreakpointDown,
  getCgBreakpointUp,
  scrollIntoViewCitationCard,
} from '../utils';
import { CITATION_SELECT_HEIGHT } from '../constants/common.constants';
import { initialize } from './EditForm/actionCreators';
import { useEditFormDispatch } from './EditForm/context';

const trimText = (text: string, length = 42) =>
  text.length > length ? `${text.substring(0, length)}...` : text;

/* const useStyles = makeStyles((theme) => ({
  button: {
    minWidth: '55px',
    width: '55px',
    borderRadius: '50px',
    height: 23,
  },
  discription: {
    fontSize: '11px',
    color: '#000000',
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  heading: {
    fontWeight: 600,
    fontSize: '13px',
    color: '#000000',
  },
  resultContainer: {
    border: `1px solid ${theme.palette.divider}`,
    height: '120px',
    padding: '10px 10px 10px 16px',
    borderRadius: '5px',
    marginBottom: '5px',
  },
}));

 function CitaionWebResult(props: any) {
  const classes = useStyles();
  return (
    <Grid container item className={classes.resultContainer}>
      <Grid container item justify="space-between">
        <Grid item>
          <Typography
            style={{
              fontWeight: 600,
              fontSize: '11px',
              color: '#2971FE',
              cursor: 'pointer',
            }}>
            {props.source}
          </Typography>
        </Grid>
        <Grid item>
          <Grid container>
            <Button
              className={classes.button}
              color="primary"
              variant="contained"
              style={{
                marginRight: '8px',
              }}>
              Cite
            </Button>
            <Button color="primary" variant="outlined" className={classes.button}>
              Edit
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Typography className={classes.heading}>{props.heading}</Typography>
      <Typography className={classes.discription}>{props.discription}</Typography>
    </Grid>
  );
} */

const useCardStyles = makeStyles((theme) => ({
  buttonWrapper: {
    display: 'flex',
    position: 'absolute',
    top: 10,
    right: 10,
    [getCgBreakpointDown(theme)]: {
      position: 'static',
      flexDirection: 'column',
      flex: 0,
    },
  },
  button: {
    minWidth: '55px',
    width: '55px',
    borderRadius: '50px',
    height: 23,
    fontSize: '13px',
    fontWeight: 600,
    [getCgBreakpointUp(theme)]: {
      '&:first-child': {
        marginRight: '8px',
      },
    },
    [getCgBreakpointDown(theme)]: {
      margin: '4px 0',
    },
  },
  titleWrapper: {
    maxWidth: 'calc(100% - 120px)',
    [getCgBreakpointDown(theme)]: {
      maxWidth: '100%',
    },
  },
  title: {
    fontWeight: 600,
    fontSize: '14px',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    hyphens: 'auto',
    [getCgBreakpointDown(theme)]: {
      fontSize: '13px',
    },
  },
  discription: {
    fontSize: '13px',
    marginTop: '5px',
    // maxWidth: 'calc(100% - 130px)',
    [getCgBreakpointDown(theme)]: {
      fontSize: '12px',
    },
  },
  heading: {
    fontWeight: 600,
    fontSize: '13px',
    color: '#000000',
  },
  resultContainer: {
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
    padding: '10px 10px 10px 16px',
    borderRadius: '5px',
    marginBottom: '7px',
    display: 'block',
    [getCgBreakpointDown(theme)]: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    },
  },
  resultInfoContainer: {
    [getCgBreakpointDown(theme)]: {
      paddingRight: 4,
      maxWidth: 'calc(100% - 55px - 4px)',
    },
  },
  metaInfoWrapper: {
    display: 'flex',
    [getCgBreakpointDown(theme)]: {
      flexDirection: 'column',
    },
  },
  metaInfo: {
    maxWidth: 'calc(50% - 35px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [getCgBreakpointDown(theme)]: {
      maxWidth: '100%',
    },
  },
  pipe: {
    margin: '0px 5px',
    color: '#C2C2C2',
    fontSize: '10px',
  },
  metaInfoLabel: {
    fontSize: '10px',
    color: theme.palette.text.secondary,
  },
  metaInfoText: {
    fontSize: '10px',
    color: theme.palette.text.primary,
    // display: 'inline-block',
  },
  btnLoaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

function CitaionResult(props: CitationData) {
  const classes = useCardStyles();
  const dispatch = useDispatch();
  const citationSource = useCitationGeneratorState((state) => state.searchType);
  const citationStyle = useSelector((state: RootState) => state.ctgr.citationStyle);
  const citationGeneratorDispatch = useCitationGeneratorDispatch();
  const editFormDispatch = useEditFormDispatch();
  const [showAuthorTooltop, setShowAuthorTooltop] = useState(false);
  const [showPublisherTooltip, setShowPublisherTooltip] = useState(false);
  const authorRef = useRef<HTMLSpanElement | null>(null);
  const publisherRef = useRef<HTMLSpanElement | null>(null);
  const [isCitedDataLoading, setIsCitedDataLoading] = useState(false);
  const isSmallScreen = useMediaQuery<Theme>((theme) => getCgBreakpointDown(theme));

  const handleButtonClick = (isEdit?: boolean) => {
    const citation = {
      ...props,
      refId: props.refId || generateRandomId(),
      id: props.id || null,
      citationSource,
    };

    if (isEdit) {
      citationGeneratorDispatch(toggleCitationGeneratorEditModal(true, citation));
      editFormDispatch(initialize(citation));
    } else {
      setIsCitedDataLoading(true);
      generateCitations({ style: citationStyle, citations: [citation] })
        .then((response) => {
          if (response.style !== citationStyle) {
            // TODO: show/throw error
          }
          const citedData = convertCitedDataResponse(response);
          dispatch(updateCitedData(citedData));
          dispatch(addCitationToList(citation));
          dispatch(rebuildCitationNumbers());
          citationGeneratorDispatch(stopSearchMode());
          scrollIntoViewCitationCard(citation.refId);
          citationGeneratorDispatch(toggleInplaceCreateForm(false));
        })
        .catch((e) => {
          openToastr('Unable to use selected citation style!');
        })
        .finally(() => {
          setIsCitedDataLoading(false);
        });
    }
  };

  useEffect(() => {
    if (authorRef.current && authorRef.current.scrollWidth > authorRef.current.offsetWidth) {
      setShowAuthorTooltop(true);
    }
    if (
      publisherRef.current &&
      publisherRef.current.offsetWidth < publisherRef.current.scrollWidth
    ) {
      setShowPublisherTooltip(true);
    }
  }, [props]);

  /* ${index === 1 && props.contributors?.length > 2 ? '...' : ''} */

  const authorsNames = props.contributors
    .filter((contributor) => contributor.contributorType === ContributorTypes.AUTHORS)
    // .slice(0, 2)
    .reduce((authorName, contributor) => {
      const { suffix, firstName, initials, lastName } = contributor;
      return `${authorName ? `${authorName}, ` : ''}${Object.values({
        suffix,
        firstName: firstName || initials,
        lastName,
      }).join(' ')}`;
      /* `${authorName ? `${authorName}, ` : ''}${
          contributor.suffix ? `${contributor.suffix} ` : ''
        }${
          contributor.firstName
            ? `${contributor.middleName ? `${contributor.firstName} ` : contributor.firstName}`
            : ''
        } ${
          contributor.middleName
            ? `${contributor.lastName ? `${contributor.middleName} ` : contributor.middleName}`
            : ''
        } ${contributor.lastName || ''}` */
    }, '');

  return (
    <Grid container className={classes.resultContainer}>
      <Grid item className={classes.resultInfoContainer}>
        <Grid container item justifyContent="space-between">
          <Grid item className={classes.titleWrapper}>
            <Typography className={classes.title} component="span">
              {props.publicationInfo.title ||
                (citationSource === CitationSourceEnum.WEBSITE
                  ? getShortUrl(props.publicationInfo.url || '')
                  : '')}
            </Typography>
          </Grid>
        </Grid>

        <Typography className={classes.discription}>
          {props.publicationInfo.containerTitle}
        </Typography>

        <Grid
          container
          style={{
            marginTop: props.publicationInfo?.containerTitle && !isSmallScreen ? 0 : '5px',
          }}
          className={classes.metaInfoWrapper}
        >
          {authorsNames ? (
            <Tooltip title={showAuthorTooltop || authorsNames.length > 42 ? authorsNames : ''}>
              <span className={classes.metaInfo} ref={authorRef}>
                <span className={classes.metaInfoLabel}>Author: </span>
                <span className={classes.metaInfoText}>
                  {isSmallScreen ? authorsNames : trimText(authorsNames)}
                </span>
              </span>
            </Tooltip>
          ) : (
            ''
          )}

          {props.publicationInfo.publisher ? (
            <span className={classes.metaInfo} ref={publisherRef}>
              {!isSmallScreen && authorsNames && <span className={classes.pipe}>|</span>}
              <Tooltip
                title={
                  showPublisherTooltip || props.publicationInfo.publisher.length > 42
                    ? props.publicationInfo.publisher
                    : ''
                }
              >
                <span>
                  <span className={classes.metaInfoLabel}>Publisher: </span>
                  <span className={classes.metaInfoText}>
                    {isSmallScreen
                      ? props.publicationInfo.publisher
                      : trimText(props.publicationInfo.publisher)}
                  </span>
                </span>
              </Tooltip>
            </span>
          ) : (
            ''
          )}

          {props.publicationInfo.publishDate?.year ? (
            <span className={classes.metaInfo}>
              {!isSmallScreen && (props.publicationInfo.publisher || authorsNames) && (
                <span className={classes.pipe}>|</span>
              )}
              <span className={classes.metaInfoLabel}>Year: </span>
              <span className={classes.metaInfoText}>
                {props.publicationInfo.publishDate?.year}
              </span>
            </span>
          ) : (
            ''
          )}
        </Grid>
      </Grid>

      <Grid item className={classes.buttonWrapper}>
        <Button
          className={classes.button}
          onClick={() => {
            if (!isCitedDataLoading) {
              handleButtonClick();
              citationGeneratorTrack.userClickedAddCitation(TriggeringActions.CITE_BUTTON);
            }
          }}
          color="primary"
          variant="contained"
        >
          {isCitedDataLoading ? (
            <span className={classes.btnLoaderContainer}>
              <CircularProgress size="18px" color="inherit" />
            </span>
          ) : (
            <span style={{ lineHeight: 1 }}>Cite</span>
          )}
        </Button>

        <Button
          color="primary"
          variant="outlined"
          className={classes.button}
          disabled={isCitedDataLoading}
          onClick={() => {
            citationGeneratorTrack.userClickedEditCitation(TriggeringActions.SEARCH_RESULT);
            handleButtonClick(true);
          }}
        >
          <span style={{ lineHeight: 1 }}>Edit</span>
        </Button>
      </Grid>
    </Grid>
  );
}

const getResultContainerHeight = (
  isCowriter: boolean,
  isSmallScreen: boolean,
  independentTool: boolean
) => {
  if (isSmallScreen) {
    return 'calc(100vh - 179px)';
  }

  if (independentTool) {
    return 'calc(100vh - 162px)';
  }

  let height;
  if (isCowriter) {
    const offset = 22;
    height = `calc(100vh - ${
      APP_BAR_HEIGHT + // 50
      parseInt(TinyToolbarContainerProps.height, 10) +
      50 + // TOOLPANE_TAB_HEIGHT
      CITATION_SELECT_HEIGHT + // 100
      offset
    }px)`;
  } else {
    height = 'calc(50vh - 5px)';
  }
  return height;
};

interface CitationsResultsContainer {
  net_scroll_cw_height?: string;
}

export default function CitaionsResultsContainer(props: CitationsResultsContainer) {
  const router = useRouter();
  const isCowriter = router.pathname.includes('/co-writer');
  const citaionSearchResults = useCitationGeneratorState((state) => state.citations);
  const isLastPage = useCitationGeneratorState((state) => state.isLastPage);
  const pageNo = useCitationGeneratorState((state) => state.pageNo);

  const isLoadingResults = useCitationGeneratorState((state) => state.isLoadingResults);
  const citationGeneratorDispatch = useCitationGeneratorDispatch();
  const isSearchMode = useCitationGeneratorState((state) => state.isSearchMode);
  const isSmallScreen = useMediaQuery<Theme>((theme) => getCgBreakpointDown(theme));
  const independentTool = useSelector((state: RootState) => state.common.independentTool);

  return (
    <BottomScrollListener
      onBottom={() => {
        if (!isLastPage && !isLoadingResults) {
          citationGeneratorDispatch(incrementPageNo());
        }
      }}
      offset={50}
    >
      {(scrollRef) => (
        <Grid container>
          <Grid
            ref={scrollRef as React.RefObject<HTMLDivElement>}
            container
            style={{
              display: 'block',
              overflow: 'auto',
              maxHeight: isCowriter
                ? props.net_scroll_cw_height
                : getResultContainerHeight(isCowriter, isSmallScreen, independentTool),
              height: isCowriter
                ? props.net_scroll_cw_height
                : getResultContainerHeight(isCowriter, isSmallScreen, independentTool),
              paddingRight: '10px',
              paddingBottom: 40,
            }}
          >
            {citaionSearchResults.length > 0 || isLoadingResults ? (
              <>
                {!(pageNo === 1 && isLoadingResults) &&
                  citaionSearchResults.map((citation, index) => (
                    <CitaionResult {...citation} key={index} />
                  ))}
                {isLoadingResults && (
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      height: pageNo === 1 ? '100%' : undefined,
                    }}
                  >
                    <CircularProgress />
                  </Grid>
                )}
              </>
            ) : null}
          </Grid>

          {(!isCowriter || isSearchMode) && (
            <Grid
              container
              style={{
                height: 40,
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 57.95%)',
                position: 'absolute',
                bottom: 0,
                width: 'calc(100% - 36px)',
                // background: 'linear-gradient(180deg,rgba(255,255,255,0) 0%, #FFFFFFF 48.6%)',
              }}
            />
          )}
        </Grid>
      )}
    </BottomScrollListener>
  );
}
